import { useTranslation } from 'react-i18next';
import { AlertCircle, Info } from 'lucide-react';
import { useDailyStats, DAILY_STATS_DAYS } from '@/hooks/use-daily-stats';
import { useMerchantScope } from '@/hooks/use-merchant-scope';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
  Skeleton,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui';
import { getIntlLocale } from '@/lib/utils/format';
import type { DailyStatsDay } from '@/types/dashboard-stats';

/**
 * Daily-stats widget (Spec 469) — compact number table for the dashboard:
 * TX created / new subs / conversion per day, cancellations set apart below.
 * Full-width card under the three existing dashboard cards.
 */

const PLACEHOLDER = '—'; // em dash

/**
 * Column label from the `day` string parts (Spec trap: `new Date('YYYY-MM-DD')`
 * is UTC midnight and could shift the day in the browser's TZ). App convention
 * is day-before-month: de → `01.07.`, en → `01/07`.
 */
function formatDayLabel(day: string, locale: string): string {
  const match = /^\d{4}-(\d{2})-(\d{2})$/.exec(day);
  if (!match) return PLACEHOLDER;
  const [, month, dayOfMonth] = match;
  return locale === 'de-DE' ? `${dayOfMonth}.${month}.` : `${dayOfMonth}/${month}`;
}

function formatCount(value: number, locale: string): string {
  return new Intl.NumberFormat(locale).format(value);
}

/** Conversion as percent with one decimal (0.041 → de `4,1 %` / en `4.1%`); null → em dash. */
function formatConversion(rate: number | null, locale: string): string {
  if (rate === null) return PLACEHOLDER;
  return new Intl.NumberFormat(locale, {
    style: 'percent',
    minimumFractionDigits: 1,
    maximumFractionDigits: 1,
  }).format(rate);
}

export function DailyStatsCard() {
  const { t } = useTranslation('dashboard');
  const { showMerchant } = useMerchantScope();
  const { data, isLoading } = useDailyStats();
  const locale = getIntlLocale();

  const scopeLabel = showMerchant ? t('dailyStats.scopePlatform') : t('dailyStats.scopeMerchant');

  // never-throw contract: the query only errors on auth/param bugs; data errors
  // arrive as `unavailable`. Treat a (theoretical) query error like unavailable.
  const unavailable = !isLoading && (!data || data.unavailable);
  const truncated = !!data?.truncated && !unavailable;
  // BFF contract: days is always N zero-filled entries, even at unavailable.
  // Fallback covers only the theoretical query-error case (no data at all).
  const days: DailyStatsDay[] =
    data?.days && data.days.length > 0
      ? data.days
      : Array.from({ length: DAILY_STATS_DAYS }, (_, i) => ({
          day: `fallback-${i}`,
          txCreated: 0,
          txTerminal: 0,
          txSuccess: 0,
          conversionRate: null,
          subsCreated: 0,
          subsCancelled: 0,
        }));

  const cell = (value: string) => (unavailable ? PLACEHOLDER : value);

  return (
    <Card className="md:col-span-2 lg:col-span-3" data-testid="daily-stats-card">
      <CardHeader className="pb-2">
        <CardTitle className="text-sm font-medium">
          {t('dailyStats.title', { days: DAILY_STATS_DAYS })}
        </CardTitle>
        <CardDescription>{scopeLabel}</CardDescription>
      </CardHeader>
      <CardContent>
        {isLoading ? (
          <div className="space-y-2" data-testid="daily-stats-skeleton">
            <Skeleton className="h-8 w-full" />
            <Skeleton className="h-8 w-full" />
            <Skeleton className="h-8 w-full" />
            <Skeleton className="h-8 w-full" />
          </div>
        ) : (
          <>
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead />
                  {days.map((d, i) => (
                    <TableHead key={d.day} className="text-right">
                      {i === days.length - 1
                        ? t('dailyStats.today')
                        : formatDayLabel(d.day, locale)}
                    </TableHead>
                  ))}
                </TableRow>
              </TableHeader>
              <TableBody>
                <TableRow>
                  <TableCell className="text-muted-foreground">
                    {t('dailyStats.rows.txCreated')}
                  </TableCell>
                  {days.map((d) => (
                    <TableCell key={d.day} className="text-right font-medium tabular-nums">
                      {cell(formatCount(d.txCreated, locale))}
                    </TableCell>
                  ))}
                </TableRow>
                <TableRow>
                  <TableCell className="text-muted-foreground">
                    {t('dailyStats.rows.subsCreated')}
                  </TableCell>
                  {days.map((d) => (
                    <TableCell key={d.day} className="text-right font-medium tabular-nums">
                      {cell(formatCount(d.subsCreated, locale))}
                    </TableCell>
                  ))}
                </TableRow>
                <TableRow>
                  <TableCell className="text-muted-foreground">
                    {t('dailyStats.rows.conversion')}
                  </TableCell>
                  {days.map((d) => (
                    <TableCell key={d.day} className="text-right font-medium tabular-nums">
                      {cell(formatConversion(d.conversionRate, locale))}
                    </TableCell>
                  ))}
                </TableRow>
                {/* Kündigungen: set apart via a stronger top border — same table, own section. */}
                <TableRow className="border-t-2">
                  <TableCell className="text-muted-foreground">
                    {t('dailyStats.rows.subsCancelled')}
                  </TableCell>
                  {days.map((d) => (
                    <TableCell key={d.day} className="text-right font-medium tabular-nums">
                      {cell(formatCount(d.subsCancelled, locale))}
                    </TableCell>
                  ))}
                </TableRow>
              </TableBody>
            </Table>
            {unavailable && (
              <p className="mt-2 flex items-center gap-1.5 text-xs text-muted-foreground">
                <AlertCircle className="h-3.5 w-3.5 shrink-0" />
                {t('dailyStats.footnoteUnavailable')}
              </p>
            )}
            {truncated && (
              <p className="mt-2 flex items-center gap-1.5 text-xs text-muted-foreground">
                <Info className="h-3.5 w-3.5 shrink-0" />
                {t('dailyStats.footnoteTruncated')}
              </p>
            )}
          </>
        )}
      </CardContent>
    </Card>
  );
}
